<template>
  <div>
    <div class="fish-counter">
      <button class="fish-counter__button purchase-fish" @click="handlePurchaseFish">
        <img class="fish-counter__fish-icon" src="@/assets/img/icon/fish_icon.png" alt="Fish" />
        <div>{{ formattedCurrentFish }}</div>
      </button>
      <!-- 無料プランの場合はボタン表示 -->
      <template v-if="isFree">
        <button class="fish-counter__button upgrade" @click="handleAddClick">
          <div class="fish-counter__premium-icon-wrapper">
            <img src="@/assets/img/icon/premium.png" alt="プレミアム" class="fish-counter__premium-icon" />
          </div>
          <span class="fish-counter__button-text">アップグレード</span>
        </button>
      </template>
      <!-- プレミアムユーザーの場合はプレミアムアイコンのみ表示 -->
      <template v-else>
        <img src="@/assets/img/icon/premium.png" alt="プレミアム" width="24" height="24" />
      </template>
    </div>

    <!-- コーチマークの表示 -->
    <div v-if="isShowCoachMark" class="fish-counter__coach-mark">
      <div class="fish-counter__coach-mark-content">
        <div>もっとほしいにゃ！</div>
        <CatIcon :size="catIconSize" :writerType="writerType?.tendency" class="fish-counter__coach-mark-cat-icon" />
        <button class="fish-counter__coach-mark-close" @click="handleCloseCoachMark">×</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { mapGetters, mapActions } from "vuex";
import { Plan } from "@/lib/models/user";
import { NolaAnalytics, NolaItemId } from "@/lib/utils/analytics";
import { AssistAnalytics } from "@/lib/utils/analytics/assist";
import CatIcon from "@/components/atoms/CatIcon.vue";
import { useTabPurchaseSync } from "@/composables/useTabPurchaseSync";

export default Vue.extend({
  name: "FishCounter",
  components: {
    CatIcon,
  },
  data() {
    return {
      nolaAnalytics: new NolaAnalytics(this.$gtm, this.$store),
      assistAnalytics: null as AssistAnalytics | null,
      catIconSize: "small",
      // Composable関数の参照を保持
      tabPurchaseSyncFunctions: null as any,
    };
  },
  computed: {
    ...mapGetters("fishModule", ["currentFish", "isLoading", "error"]),
    ...mapGetters("billingModule", ["plan"]),
    ...mapGetters("fishModule", ["isShowFishPurchaseCoachMark"]),
    ...mapGetters("writerTypeResultModule", {
      writerType: "writerType",
    }),
    /**
     * フォーマット済みのcurrentFish
     */
    formattedCurrentFish(): string {
      return (this.currentFish as number).toLocaleString("ja-JP");
    },
    /**
     * 無料プランかどうかを判定
     */
    isFree() {
      const currentPlan = this.plan as Plan;
      return currentPlan === Plan.free;
    },
    isShowCoachMark() {
      return this.isShowFishPurchaseCoachMark;
    },
  },
  watch: {
    currentFish: {
      handler(newValue: number) {
        // currentFish が 5,000 未満になった時にコーチマークを表示
        if (newValue < 5000) {
          this.$store.dispatch("fishModule/showFishPurchaseCoachMark");
        }
      },
    },
  },
  async mounted() {
    // アナリティクス初期化
    this.assistAnalytics = new AssistAnalytics(this.$gtm);

    // Composableを初期化
    this.initializeTabPurchaseSync();

    // コンポーネントがマウントされた時にFishデータを取得
    try {
      await this.fetchFishData();
    } catch (error) {
      console.error("Fish データの初期取得に失敗:", error);
    }
  },
  beforeDestroy() {
    // TabPurchaseSyncのクリーンアップ
    if (this.tabPurchaseSyncFunctions?.cleanup) {
      this.tabPurchaseSyncFunctions.cleanup();
    }
  },
  methods: {
    ...mapActions("fishModule", ["fetchFishData"]),
    async handleAddClick(): Promise<void> {
      try {
        // アナリティクス: プレミアムボタンクリックイベントの発火
        if (this.assistAnalytics) {
          this.assistAnalytics.trackPremiumButtonClick();
        }

        // プレミアムボタンクリック時にSubscriptionAnnounceページを別タブで開く
        // 執筆中の作業を中断させないよう配慮
        const subscriptionUrl = this.$router.resolve({ name: "subscriptionAnnounce" }).href;
        window.open(subscriptionUrl, "_blank");
      } catch (error) {
        console.error("プレミアムボタンクリック処理に失敗:", error);
      }
    },
    async handlePurchaseFish(): Promise<void> {
      // アナリティクス: Fish 購入ボタンクリックイベントの発火
      this.nolaAnalytics.logButtonEvent(NolaItemId.AssistFooterPurchaseFish);

      // Fish 購入ページを別タブで開く
      const subscriptionUrl = this.$router.resolve({ name: "purchaseFish" }).href;
      window.open(subscriptionUrl, "_blank");
    },
    handleCloseCoachMark(): void {
      this.$store.dispatch("fishModule/closeFishPurchaseCoachMark");
    },
    // タブ復帰同期の初期化（mounted後に実行）
    initializeTabPurchaseSync(): void {
      // Composableを初期化してthisが利用可能な同期コールバックを設定
      this.tabPurchaseSyncFunctions = useTabPurchaseSync(
        async () => {
          // thisが利用可能な状態でFishデータを取得
          try {
            await this.fetchFishData();
          } catch (error) {
            console.error("[FishCounter] Failed to fetch Fish data:", error);
          }
        },
        { enableDebugLog: false }
      );
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.fish-counter {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--color-bg-dark);
  padding: 12px;
}

.fish-counter__button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  line-height: 1.5;
  color: var(--color-text-normal);
  background: var(--color-background-normal);
  border: none;
  border-radius: var(--radius-lg);
  padding: 4px 8px;
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: 1px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }

  &.purchase-fish {
    color: var(--color-text-normal);
    background: var(--color-background-normal);
    padding: 4px 10px 4px 4px;
  }

  &.upgrade {
    color: var(--color-text-on-fixed-bg);
    background: var(--color-brand-premium);
    padding: 4px 10px 4px 6px;
  }
}

.fish-counter__fish-icon {
  width: 32px;
  height: 32px;
}

.fish-counter__premium-icon-wrapper {
  width: 28px;
  height: 28px;
  padding: 4px;
  margin: 2px;
  box-sizing: border-box;
  flex-shrink: 0;
  background: var(--color-background-normal);
  background-clip: padding-box;
  border-radius: var(--radius-circle);
  display: flex;
  align-items: center;
  justify-content: center;
}

.fish-counter__premium-icon {
  width: 20px;
  height: 20px;
}

.fish-counter__button-text {
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  color: inherit;
}

.fish-counter__coach-mark {
  position: absolute;
  top: -53px;
  left: 10px;
  width: 75%;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
  z-index: 1;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);

  &::before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 20%;
    border-style: solid;
    border-width: 16px 6px 0 6px;
    border-color: var(--color-background-normal) transparent transparent;
    translate: -50% 100%;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
  }
}

.fish-counter__coach-mark-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 16px;
}

.fish-counter__coach-mark-cat-icon {
  position: absolute;
  bottom: 5px;
  right: 15%;
}

.fish-counter__coach-mark-close {
  position: absolute;
  top: 4px;
  right: 10px;
  width: 20px;
  height: 20px;
  background: none;
  border: none;
  border-radius: var(--radius-circle);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-low);

  @include hover-overlay;
}
</style>
